import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { ArrowLeft, Send, BookOpen, Sparkles, MessageCircle, HelpCircle, Loader2, BookText, Lightbulb, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const ChapterLearn = () => {
  const { subject, chapterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tutorName = user?.tutor_name || 'Buddy';
  const [chapter, setChapter] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState('explain');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        const historyRes = await apiService.getTutorHistory(chapterId);
        const history = historyRes.data.history || [];
        const mapped = [];
        history.forEach(h => {
          mapped.push({ role: 'user', text: h.user_message, mode: h.mode });
          mapped.push({ role: 'ai', text: h.ai_response, mode: h.mode });
        });
        setMessages(mapped);

        // Get chapter info from chapters list
        const chaptersRes = await apiService.getChapters(subject);
        const ch = chaptersRes.data.chapters.find(c => c.id === chapterId);
        setChapter(ch);

        // Auto-send welcome if no history
        if (history.length === 0) {
          setLoading(true);
          const res = await apiService.tutorChat({
            chapter_id: chapterId,
            message: `Hi! Please introduce this chapter "${ch?.name || 'this chapter'}" to me in a fun and easy way. What will I learn?`,
            mode: 'explain'
          });
          setMessages([
            { role: 'user', text: `Tell me about this chapter!`, mode: 'explain' },
            { role: 'ai', text: res.data.response, mode: 'explain' }
          ]);
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
      } finally { setInitialLoading(false); }
    };
    init();
  }, [subject, chapterId]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async (text, msgMode) => {
    if (!text.trim()) return;
    const userMsg = { role: 'user', text, mode: msgMode || mode };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await apiService.tutorChat({
        chapter_id: chapterId,
        message: text,
        mode: msgMode || mode,
      });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response, mode: msgMode || mode }]);
    } catch (e) {
      toast.error('AI tutor is temporarily unavailable');
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I had a small hiccup! Try asking me again.', mode: 'error' }]);
    } finally { setLoading(false); }
  };

  const quickActions = [
    { label: 'Explain Simply', icon: <Lightbulb className="w-4 h-4" />, mode: 'explain', prompt: `Explain the key concepts of "${chapter?.name}" in the simplest way possible with examples.` },
    { label: 'Tell a Story', icon: <BookText className="w-4 h-4" />, mode: 'story', prompt: `Tell me a fun story that teaches the concepts of "${chapter?.name}".` },
    { label: 'I Have a Doubt', icon: <HelpCircle className="w-4 h-4" />, mode: 'doubt', prompt: '' },
    { label: 'Give Examples', icon: <Sparkles className="w-4 h-4" />, mode: 'explain', prompt: `Give me 5 real-life examples related to "${chapter?.name}" that I can see around me.` },
    { label: 'Summary', icon: <GraduationCap className="w-4 h-4" />, mode: 'explain', prompt: `Give me a quick summary of everything important in "${chapter?.name}" with key points to remember.` },
  ];

  const subjectColors = {
    mathematics: { bg: 'from-blue-500 to-indigo-600', light: 'bg-blue-50', accent: 'bg-blue-500' },
    science: { bg: 'from-green-500 to-emerald-600', light: 'bg-green-50', accent: 'bg-green-500' },
    english: { bg: 'from-purple-500 to-violet-600', light: 'bg-purple-50', accent: 'bg-purple-500' },
  };
  const colors = subjectColors[subject] || subjectColors.mathematics;

  if (initialLoading) return (
    <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-16 h-16 text-[#4D96FF] animate-spin mx-auto mb-4" />
        <p className="text-lg font-semibold text-[#64748B]">Starting your AI tutor...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F8FB] flex flex-col" data-testid="chapter-learn-page">
      {/* Header */}
      <div className={`bg-gradient-to-r ${colors.bg} text-white`}>
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(`/chapters/${subject}`)} className="text-white/80 hover:text-white" data-testid="back-btn">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-xl font-bold">{chapter?.name}</h1>
                <p className="text-sm text-white/80">AI Tutor | {subject.charAt(0).toUpperCase() + subject.slice(1)}</p>
              </div>
            </div>
            <button onClick={() => navigate(`/chapters/${subject}/${chapterId}/test`)} data-testid="take-test-btn"
              className="px-5 py-2 bg-white/20 backdrop-blur-md rounded-xl font-bold text-sm hover:bg-white/30 transition-all border border-white/20">
              Take Chapter Test
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {quickActions.map((action, i) => (
              <button key={i} onClick={() => action.prompt ? sendMessage(action.prompt, action.mode) : setMode('doubt')}
                data-testid={`quick-action-${i}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all border-2 ${
                  mode === action.mode && !action.prompt ? 'bg-[#4D96FF] text-white border-[#4D96FF]' : 'bg-[#F4F8FB] text-[#64748B] border-slate-200 hover:border-[#4D96FF]'
                }`}>
                {action.icon} {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
          {messages.length === 0 && !loading && (
            <div className="text-center py-16">
              <div className={`w-24 h-24 ${colors.accent} rounded-3xl flex items-center justify-center mx-auto mb-6`}>
                <BookOpen className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-black text-[#1E293B] mb-3">Ready to Learn!</h2>
              <p className="text-lg text-[#64748B] mb-6">Ask me anything about <strong>{chapter?.name}</strong></p>
              <p className="text-[#64748B]">Try the quick actions above or type your question below</p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${
                msg.role === 'user'
                  ? 'bg-[#4D96FF] text-white rounded-2xl rounded-br-md px-5 py-3'
                  : 'bg-white border-2 border-slate-100 rounded-2xl rounded-bl-md px-5 py-4 shadow-sm'
              }`}>
                {msg.role === 'ai' && (
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-6 h-6 ${colors.accent} rounded-lg flex items-center justify-center`}>
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs font-bold text-[#64748B] uppercase">
                      {msg.mode === 'story' ? `${tutorName} - Story Mode` : msg.mode === 'doubt' ? `${tutorName} - Doubt Solver` : `${tutorName} - AI Tutor`}
                    </span>
                  </div>
                )}
                <div className={`whitespace-pre-wrap leading-relaxed ${msg.role === 'user' ? 'font-medium' : 'text-[#1E293B]'}`}
                  dangerouslySetInnerHTML={{ __html: formatResponse(msg.text) }} />
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border-2 border-slate-100 rounded-2xl px-5 py-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-[#4D96FF] animate-spin" />
                  <span className="text-[#64748B] font-medium">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Bar */}
      <div className="bg-white border-t-2 border-slate-100">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex gap-3">
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
              className="flex-1 p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:border-[#4D96FF] focus:ring-4 focus:ring-[#4D96FF]/10 outline-none font-medium"
              placeholder={mode === 'doubt' ? "Type your doubt here..." : "Ask me anything about this chapter..."}
              disabled={loading} data-testid="tutor-input" />
            <button onClick={() => sendMessage(input)} disabled={loading || !input.trim()} data-testid="send-btn"
              className={`px-6 ${colors.accent} text-white rounded-2xl font-bold border-b-4 border-black/20 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50 flex items-center gap-2`}>
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatResponse = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#1E293B]">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold text-[#1E293B] mt-3 mb-1">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-[#1E293B] mt-3 mb-1">$1</h2>')
    .replace(/^- (.*$)/gm, '<div class="flex gap-2 ml-2"><span class="text-[#4D96FF] font-bold">•</span><span>$1</span></div>')
    .replace(/^\d+\. (.*$)/gm, '<div class="flex gap-2 ml-2"><span class="text-[#4D96FF] font-bold">$&</span></div>')
    .replace(/\n/g, '<br/>');
};

export default ChapterLearn;
